import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from '@app/services/notification.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import QRCode from 'qrcode';
import { AuthStore } from '@app/core/auth/auth.store';
import { CurrentUserService, Permission } from '@app/core/services';
import { LocaleApi, UserApi } from '@app/data/api';
import {
  CountryDto,
  CurrentUserDto,
  MfaEnrollmentSetupDto,
  MfaPolicyType,
  UpdateUserViewDto,
  UserActiveSessionDto,
  UserApiTokenDto,
} from '@app/data/dto';
import { ButtonSmallComponent } from '@app/shared/controls/app-button-small/app-button-small';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { AppModal } from '@app/shared/controls/app-modal/app-modal';
import { DropdownComponent } from '../../controls/app-dropdown/app-dropdown';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { SetupMfaComponent } from './setup-mfa/setup-mfa.component';
import { ChangeEmailComponent } from './change-email/change-email.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { ApiTokenComponent } from './api-token/api-token.component';

type SecurityModalType = 'email' | 'password' | 'mfa' | 'tokens';

@Component({
  selector: 'app-profile-settings',
  imports: [AppIcon, CommonModule, FormsModule, AppModal, ButtonSmallComponent, DropdownComponent, ConfirmModalComponent, TranslatePipe, SetupMfaComponent, ChangeEmailComponent, ChangePasswordComponent, ApiTokenComponent],
  templateUrl: './profile-settings.html',
  styleUrl: './profile-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSettings implements OnChanges {
  private readonly userApi = inject(UserApi);
  private readonly localeApi = inject(LocaleApi);
  private readonly authStore = inject(AuthStore);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('contentContainer') private readonly contentContainer?: ElementRef<HTMLElement>;

  protected readonly submitting = signal(false);
  protected readonly loading = signal(false);
  protected readonly activeSection = signal('personal-data');
  protected readonly hoveredNavItemId = signal<string | null>(null);
  protected readonly hoveredSecurityNavItemId = signal<SecurityModalType | null>(null);
  protected readonly activeSecurityModal = signal<SecurityModalType | null>(null);
  protected readonly showDiscardChangesConfirm = signal(false);
  protected readonly mfaWizardStep = signal<1 | 2 | 3>(1);
  protected readonly mfaBusy = signal(false);
  protected readonly sessionsLoadingMore = signal(false);
  protected readonly canManageApiTokens = computed(() => this.authStore.hasAnyPermission([Permission.PERM_USER_SPECIAL_API_TOKEN_ACCESS]));
  protected readonly sessionPageSize = 5;

  protected readonly navItems = [
    { id: 'personal-data', labelKey: 'app.profileSettings.nav.personalData' },
    { id: 'accessibility', labelKey: 'app.profileSettings.nav.accessibility' },
    { id: 'security-account', labelKey: 'app.profileSettings.nav.securityAccount' },
    { id: 'active-sessions', labelKey: 'app.profileSettings.nav.activeSessions' },
  ] as const;

  protected currentUser: CurrentUserDto | null = null;
  protected countries: CountryDto[] = [];
  protected apiTokens: UserApiTokenDto[] = [];
  protected sessions: UserActiveSessionDto[] = [];
  protected totalSessionCount = 0;

  protected profileModel = {
    firstName: '',
    middleName: '',
    lastName: '',
    countryCode: '',
    phoneNumberLocal: '',
    email: '',
    dyslexiaModeEnabled: false,
  };

  protected passwordModel = {
    currentPassword: '',
    newPassword: '',
  };

  protected securityEmailModel = {
    email: '',
  };

  protected tokenCreateModel = {
    description: '',
    expireAt: this.getDefaultTokenExpireDate(),
  };

  protected generatedTokenValue = '';
  protected mfaSetup: MfaEnrollmentSetupDto | null = null;
  protected mfaQrCodeDataUrl = '';
  protected mfaCode = '';

  private initialProfileSnapshot = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || !changes['open']) {
      return;
    }

    void this.loadData().then(() => {
      queueMicrotask(() => {
        this.scrollToSection('personal-data');
      });
    });
  }

  protected requestClose(): void {
    if (this.hasUnsavedChanges()) {
      this.showDiscardChangesConfirm.set(true);
      return;
    }

    this.close();
  }

  protected close(): void {
    this.showDiscardChangesConfirm.set(false);
    this.activeSecurityModal.set(null);
    this.closed.emit();
  }

  protected cancelDiscardChanges(): void {
    this.showDiscardChangesConfirm.set(false);
  }

  protected confirmDiscardChanges(): void {
    this.close();
  }

  protected onMainBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.requestClose();
    }
  }

  protected hasUnsavedChanges(): boolean {
    return this.buildProfileSnapshot() !== this.initialProfileSnapshot;
  }

  protected getSaveButtonLabelKey(): string {
    return this.hasUnsavedChanges()
      ? 'common.actions.save'
      : 'common.actions.close';
  }

  protected async onSaveOrClose(): Promise<void> {
    if (this.hasUnsavedChanges()) {
      await this.onSave();
      return;
    }

    this.close();
  }

  protected openSecurityModal(type: SecurityModalType): void {
    if (type === 'mfa' && this.isMfaPolicyDisabled()) {
      return;
    }

    if (type === 'tokens' && !this.canManageApiTokens()) {
      return;
    }

    this.activeSecurityModal.set(type);

    if (type === 'mfa') {
      this.mfaWizardStep.set(this.mfaSetup ? 2 : 1);
    }
  }

  protected closeSecurityModal(): void {
    this.activeSecurityModal.set(null);
    this.mfaWizardStep.set(1);
  }

  protected onContentScroll(): void {
    const container = this.contentContainer?.nativeElement;
    if (!container) {
      return;
    }

    const sectionIds = this.navItems.map((item) => item.id);
    const targetLine = container.scrollTop + 120;

    let selected = sectionIds[0];
    for (const id of sectionIds) {
      const section = container.querySelector<HTMLElement>(`#${id}`);
      if (!section) {
        continue;
      }

      if (section.offsetTop <= targetLine) {
        selected = id;
      }
    }

    this.activeSection.set(selected);
  }

  protected scrollToSection(sectionId: string): void {
    const container = this.contentContainer?.nativeElement;
    const target = container?.querySelector<HTMLElement>(`#${sectionId}`);
    if (!container || !target) {
      return;
    }

    container.scrollTo({
      top: target.offsetTop - 90,
      behavior: 'smooth',
    });
    this.activeSection.set(sectionId);
  }

  protected onNavItemMouseEnter(itemId: string): void {
    this.hoveredNavItemId.set(itemId);
  }

  protected onNavItemMouseLeave(itemId: string): void {
    if (this.hoveredNavItemId() === itemId) {
      this.hoveredNavItemId.set(null);
    }
  }

  protected onSecurityNavItemMouseEnter(itemId: SecurityModalType): void {
    this.hoveredSecurityNavItemId.set(itemId);
  }

  protected onSecurityNavItemMouseLeave(itemId: SecurityModalType): void {
    if (this.hoveredSecurityNavItemId() === itemId) {
      this.hoveredSecurityNavItemId.set(null);
    }
  }

  protected getSidebarNavIcon(itemId: string): string {
    if (itemId === 'personal-data') {
      return 'icon-user-regular.svg';
    }

    if (itemId === 'security-account') {
      return 'icon-lock-regular.svg';
    }

    if (itemId === 'accessibility') {
      return 'icon-interlining-regular.svg';
    }

    return 'icon-shield-regular.svg';
  }

  protected getSidebarNavIconColor(itemId: string): string {
    return this.activeSection() === itemId || this.hoveredNavItemId() === itemId
      ? 'var(--safegpt-primary)'
      : 'var(--safegpt-text-muted)';
  }

  protected getSecuritySidebarNavIcon(itemId: SecurityModalType): string {
    if (itemId === 'email') {
      return 'icon-mail-regular.svg';
    }

    if (itemId === 'password') {
      return 'icon-lock-regular.svg';
    }

    if (itemId === 'mfa') {
      return 'icon-shield-regular.svg';
    }

    return 'icon-key-regular.svg';
  }

  protected getSecuritySidebarNavIconColor(itemId: SecurityModalType): string {
    return this.activeSecurityModal() === itemId || this.hoveredSecurityNavItemId() === itemId
      ? 'var(--safegpt-primary)'
      : 'var(--safegpt-text-muted)';
  }

  protected getInitials(): string {
    const firstInitial = this.profileModel.firstName.trim().charAt(0).toUpperCase();
    const lastInitial = this.profileModel.lastName.trim().charAt(0).toUpperCase();
    const combined = `${firstInitial}${lastInitial}`.trim();
    return combined || '-';
  }

  protected getFullName(): string {
    const parts = [
      this.profileModel.firstName.trim(),
      this.profileModel.middleName.trim(),
      this.profileModel.lastName.trim(),
    ].filter((part) => part.length > 0);

    return parts.join(' ') || '-';
  }

  protected getEmail(): string {
    return this.profileModel.email.trim();
  }

  protected async onSave(): Promise<void> {
    if (!this.currentUser?.id || this.submitting()) {
      return;
    }

    const profileChanged = this.buildProfileSnapshot() !== this.initialProfileSnapshot;

    if (!profileChanged) {
      this.close();
      return;
    }

    this.submitting.set(true);

    try {
      const updatePayload: UpdateUserViewDto = {
        firstName: this.profileModel.firstName.trim(),
        middleName: this.profileModel.middleName.trim() || null,
        lastName: this.profileModel.lastName.trim(),
        email: this.profileModel.email.trim(),
        dyslexiaModeEnabled: this.profileModel.dyslexiaModeEnabled,
      };

      const country = this.countries.find((item) => item.countryCode === this.profileModel.countryCode);
      if (country && this.profileModel.phoneNumberLocal.trim()) {
        updatePayload.countryCode = country.countryCode;
        updatePayload.phoneNumber = `${country.dialingCode}${this.profileModel.phoneNumberLocal.trim()}`;
      }

      await firstValueFrom(this.userApi.update(this.currentUser.id, updatePayload));
      this.notification.success(this.translate.instant('app.profileSettings.notifications.saveSuccess'));
      await this.currentUserService.getCurrentUser(true);
      await this.loadData();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.saveError'));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async createApiToken(): Promise<void> {
    if (!this.canManageApiTokens()) {
      return;
    }

    if (!this.tokenCreateModel.expireAt) {
      this.notification.warning(this.translate.instant('app.profileSettings.notifications.apiTokenExpireRequired'));
      return;
    }

    try {
      const token = await firstValueFrom(this.userApi.createApiToken({
        description: this.tokenCreateModel.description.trim() || undefined,
        expireAt: new Date(this.tokenCreateModel.expireAt).toISOString(),
      }));

      this.generatedTokenValue = token.tokenValue;
      this.tokenCreateModel = {
        description: '',
        expireAt: this.getDefaultTokenExpireDate(),
      };
      await this.loadApiTokens();
      this.cdr.markForCheck();
      this.notification.success(this.translate.instant('app.profileSettings.notifications.apiTokenCreateSuccess'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.apiTokenCreateError'));
    }
  }

  protected async deleteApiToken(tokenId: string): Promise<void> {
    if (!this.canManageApiTokens()) {
      return;
    }

    try {
      await firstValueFrom(this.userApi.deleteApiToken(tokenId));
      this.generatedTokenValue = '';
      await this.loadApiTokens();
      this.cdr.markForCheck();
      this.notification.success(this.translate.instant('app.profileSettings.notifications.apiTokenDeleteSuccess'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.apiTokenDeleteError'));
    }
  }

  protected async rotateApiToken(tokenId: string): Promise<void> {
    if (!this.canManageApiTokens()) {
      return;
    }

    try {
      const token = await firstValueFrom(this.userApi.rotateApiToken(tokenId));
      this.generatedTokenValue = token.tokenValue;
      await this.loadApiTokens();
      this.cdr.markForCheck();
      this.notification.success(this.translate.instant('app.profileSettings.notifications.apiTokenRotateSuccess'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.apiTokenRotateError'));
    }
  }

  protected async startMfaEnrollment(): Promise<void> {
    this.mfaBusy.set(true);

    try {
      this.mfaSetup = await firstValueFrom(this.userApi.beginMfaEnrollment());
      this.mfaCode = '';
      this.mfaQrCodeDataUrl = await QRCode.toDataURL(this.mfaSetup.authenticatorUri, { width: 240, margin: 1 });
      this.mfaWizardStep.set(2);
      this.cdr.markForCheck();
      this.notification.info(this.translate.instant('app.profileSettings.notifications.mfaSetupStarted'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.mfaSetupStartError'));
    } finally {
      this.mfaBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  protected async completeMfaEnrollment(): Promise<void> {
    if (!this.mfaCode.trim()) {
      this.notification.warning(this.translate.instant('app.profileSettings.notifications.mfaCodeRequired'));
      return;
    }

    try {
      await firstValueFrom(this.userApi.completeMfaEnrollment({ mfaCode: this.mfaCode.trim() }));
      this.mfaWizardStep.set(3);
      this.mfaSetup = null;
      this.mfaQrCodeDataUrl = '';
      this.mfaCode = '';
      this.notification.success(this.translate.instant('app.profileSettings.notifications.mfaEnableSuccess'));
      await this.loadCurrentUser();
      this.closeSecurityModal();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.mfaEnableError'));
    }
  }

  protected async disableMfa(): Promise<void> {
    try {
      await firstValueFrom(this.userApi.disableMfa());
      this.mfaSetup = null;
      this.mfaQrCodeDataUrl = '';
      this.mfaCode = '';
      this.mfaWizardStep.set(1);
      this.notification.success(this.translate.instant('app.profileSettings.notifications.mfaDisableSuccess'));
      await this.loadCurrentUser();
      this.closeSecurityModal();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.mfaDisableError'));
    }
  }

  protected async updateEmailAddress(): Promise<void> {
    if (!this.currentUser?.id) {
      return;
    }

    const nextEmail = this.securityEmailModel.email.trim();
    if (!nextEmail) {
      this.notification.warning(this.translate.instant('app.profileSettings.notifications.emailRequired'));
      return;
    }

    try {
      await firstValueFrom(this.userApi.update(this.currentUser.id, { email: nextEmail }));
      this.notification.success(this.translate.instant('app.profileSettings.notifications.emailUpdateSuccess'));
      await this.loadCurrentUser();
      this.closeSecurityModal();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.emailUpdateError'));
    }
  }

  protected async updatePassword(): Promise<void> {
    const currentPassword = this.passwordModel.currentPassword.trim();
    const newPassword = this.passwordModel.newPassword.trim();

    if (!currentPassword || !newPassword) {
      this.notification.warning(this.translate.instant('app.profileSettings.notifications.passwordBothRequired'));
      return;
    }

    try {
      await firstValueFrom(this.userApi.changePassword({
        currentPassword,
        newPassword,
      }));

      this.passwordModel.currentPassword = '';
      this.passwordModel.newPassword = '';
      this.notification.success(this.translate.instant('app.profileSettings.notifications.passwordUpdateSuccess'));
      this.closeSecurityModal();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.passwordUpdateError'));
    }
  }

  protected async revokeSession(sessionId: string): Promise<void> {
    try {
      await firstValueFrom(this.userApi.revokeActiveSession(sessionId));
      await this.loadSessions();
      this.notification.success(this.translate.instant('app.profileSettings.notifications.sessionRevokeSuccess'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.sessionRevokeError'));
    }
  }

  protected async revokeOtherSessions(): Promise<void> {
    try {
      await firstValueFrom(this.userApi.revokeOtherActiveSessions());
      await this.loadSessions();
      this.notification.success(this.translate.instant('app.profileSettings.notifications.otherSessionsRevokeSuccess'));
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.otherSessionsRevokeError'));
    }
  }

  protected remainingSessionCount(): number {
    return Math.max(this.totalSessionCount - this.sessions.length, 0);
  }

  protected hasMoreSessions(): boolean {
    return this.remainingSessionCount() > 0;
  }

  protected async loadMoreSessions(): Promise<void> {
    await this.loadSessions(true);
  }

  protected canEnableMfa(): boolean {
    if (!this.currentUser) {
      return false;
    }

    const policyType = this.currentUser.appUser?.company?.mfaPolicyType;
    return !this.currentUser.twoFactorEnabled && policyType !== MfaPolicyType.Disabled;
  }

  protected canDisableMfa(): boolean {
    if (!this.currentUser) {
      return false;
    }

    const policyType = this.currentUser.appUser?.company?.mfaPolicyType;
    return !!this.currentUser.twoFactorEnabled && policyType !== MfaPolicyType.Required;
  }

  protected isMfaEnabled(): boolean {
    return !!this.currentUser?.twoFactorEnabled;
  }

  protected isMfaPolicyRequired(): boolean {
    return this.currentUser?.appUser?.company?.mfaPolicyType === MfaPolicyType.Required;
  }

  protected isMfaPolicyDisabled(): boolean {
    return this.currentUser?.appUser?.company?.mfaPolicyType === MfaPolicyType.Disabled;
  }

  protected canShowMfaOption(): boolean {
    return !this.isMfaPolicyDisabled();
  }

  protected getDialingCode(countryCode: string): string {
    return this.countries.find((item) => item.countryCode === countryCode)?.dialingCode ?? '';
  }

  protected readonly countrySearchFn = (term: string, item: unknown): boolean => {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const country = item as CountryDto;
    const lower = term.toLowerCase();
    return (
      country.countryName.toLowerCase().includes(lower) ||
      country.countryCode.toLowerCase().includes(lower) ||
      country.dialingCode.toLowerCase().includes(lower)
    );
  };

  protected readonly dialCountryLabelTemplate = (item: unknown): string => {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const country = item as CountryDto;
    return country.dialingCode;
  };

  protected readonly dialCountryOptionTemplate = (item: unknown): string => {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const country = item as CountryDto;
    return `${country.dialingCode} - ${country.countryName}`;
  };

  private async loadData(): Promise<void> {
    this.loading.set(true);

    try {
      await Promise.all([
        this.loadCurrentUser(),
        this.loadCountries(),
      ]);

      await Promise.all([
        this.canManageApiTokens() ? this.loadApiTokens() : Promise.resolve(),
        this.loadSessions(),
      ]);

      this.initialProfileSnapshot = this.buildProfileSnapshot();
    } catch {
      this.notification.error(this.translate.instant('app.profileSettings.notifications.loadError'));
    } finally {
      this.loading.set(false);
    }
  }

  private async loadCurrentUser(): Promise<void> {
    this.currentUser = await this.currentUserService.getCurrentUser(true);
    if (!this.currentUser) {
      return;
    }

    const appUser = this.currentUser.appUser;
    this.profileModel.firstName = appUser?.firstName ?? '';
    this.profileModel.middleName = appUser?.middleName ?? '';
    this.profileModel.lastName = appUser?.lastName ?? '';
    this.profileModel.countryCode = appUser?.countryCode ?? '';
    this.profileModel.email = this.currentUser.email ?? '';
    this.profileModel.dyslexiaModeEnabled = appUser?.dyslexiaModeEnabled === true;
    this.securityEmailModel.email = this.currentUser.email ?? '';
    this.profileModel.phoneNumberLocal = this.extractLocalPhoneNumber(this.currentUser.phoneNumber ?? '', this.profileModel.countryCode);
  }

  private async loadCountries(): Promise<void> {
    this.countries = await firstValueFrom(this.localeApi.getCountries());

    if (!this.profileModel.countryCode && this.countries.length > 0) {
      this.profileModel.countryCode = this.countries[0].countryCode;
    }
  }

  private async loadApiTokens(): Promise<void> {
    if (!this.canManageApiTokens()) {
      this.apiTokens = [];
      this.cdr.markForCheck();
      return;
    }

    this.apiTokens = await firstValueFrom(this.userApi.getApiTokens());
    this.cdr.markForCheck();
  }

  private async loadSessions(append = false): Promise<void> {
    if (append) {
      this.sessionsLoadingMore.set(true);
    }

    try {
      const result = await firstValueFrom(this.userApi.getActiveSessions(append ? this.sessions.length : 0, this.sessionPageSize));
      this.sessions = append ? [...this.sessions, ...result.items] : result.items;
      this.totalSessionCount = result.totalCount;
      this.cdr.markForCheck();
    } finally {
      if (append) {
        this.sessionsLoadingMore.set(false);
      }
    }
  }

  private buildProfileSnapshot(): string {
    return JSON.stringify({
      firstName: this.profileModel.firstName.trim(),
      middleName: this.profileModel.middleName.trim(),
      lastName: this.profileModel.lastName.trim(),
      countryCode: this.profileModel.countryCode,
      phoneNumberLocal: this.profileModel.phoneNumberLocal.trim(),
      email: this.profileModel.email.trim(),
      dyslexiaModeEnabled: this.profileModel.dyslexiaModeEnabled,
    });
  }

  private extractLocalPhoneNumber(phoneNumber: string, countryCode: string): string {
    if (!phoneNumber || !countryCode) {
      return phoneNumber;
    }

    const dialingCode = this.getDialingCode(countryCode);
    if (!dialingCode || !phoneNumber.startsWith(dialingCode)) {
      return phoneNumber;
    }

    return phoneNumber.slice(dialingCode.length);
  }

  private getDefaultTokenExpireDate(): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 10);
  }
}
